import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const AuthContext = createContext(null);

const TOKEN_REFRESH_INTERVAL = 1 * 60 * 1000; // 4 minutes
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantDomain, setTenantDomain] = useState(null);
  
  const refreshTokenTimeoutRef = useRef(null);
  const sessionTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const updateTenantDomain = (domain) => {
    setTenantDomain(domain);
  };

  // Function to refresh the access token
  const refreshAccessToken = useCallback(async () => {
    if (!tenantDomain) {
      return;
    }
    try {
      // Replace this with your actual refresh token API call
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to refresh token');

      const data = await response.json();
      scheduleTokenRefresh();
    } catch (error) {
      console.error('Error refreshing token:', error);
      // If refresh fails, log out the user
      await logout();
    }
  }, [tenantDomain]);

  // Schedule next token refresh
  const scheduleTokenRefresh = useCallback(() => {
    if (refreshTokenTimeoutRef.current) {
      clearTimeout(refreshTokenTimeoutRef.current);
    }
    refreshTokenTimeoutRef.current = setTimeout(refreshAccessToken, TOKEN_REFRESH_INTERVAL);
  }, [refreshAccessToken]);

  // Logout handler
  const logout = useCallback(async () => {
    if (!tenantDomain) {
      return;
    }
    setUser(null);
    
    if (refreshTokenTimeoutRef.current) clearTimeout(refreshTokenTimeoutRef.current);
    if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
 
    try {
        await fetch(`${process.env.REACT_APP_API_URL}/logout/`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            credentials: 'include'
        });
        
        window.location.href = tenantDomain ? `/${tenantDomain}/login` : '/install';
    } catch (error) {
        console.error('Logout error:', error);
        // Still redirect even if backend call fails
        window.location.href = tenantDomain ? `/${tenantDomain}/login` : '/install';
    }
 }, [tenantDomain]);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      
      if (timeSinceLastActivity >= SESSION_TIMEOUT) {
        await logout();
      }
    }, 10000);
  
    return () => clearInterval(intervalId);
  }, [logout]);

  // Track user activity
  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, []);

  // Login handler
  const login = useCallback(async (userData) => {
    const user = {
      email: userData.email,
      userId: userData.sub,
      tenantId: userData.aud,
      name: `${userData.firstName} ${userData.lastName}`,
      roles: userData.roles,
      tenantDomain: userData.tenantDomain
    };

    setUser(user);
    scheduleTokenRefresh();
  }, [scheduleTokenRefresh]);

  

  // Update user data
  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  // Initialize auth state by calling /refresh on page load.
  // Tokens live in httpOnly cookies so we just need a successful refresh
  // to know the session is valid and to rehydrate the user object.
  useEffect(() => {
    const initAuth = async () => {
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const domain = pathParts[0];

      if (!domain || ['install', 'confirm-email', 'set-password'].includes(domain)) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/${domain}/refresh`, {
          method: 'POST',
          credentials: 'include',
        });

        if (!response.ok) {
          setUser(null);
          return;
        }

        const data = await response.json();
        setUser({
          email: data.email,
          userId: data.sub,
          tenantId: data.aud,
          name: `${data.firstName} ${data.lastName}`,
          roles: data.roles,
          tenantDomain: data.tenantDomain,
        });
        scheduleTokenRefresh();
      } catch (err) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    return () => {
      if (refreshTokenTimeoutRef.current) clearTimeout(refreshTokenTimeoutRef.current);
      if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
    };
  }, [scheduleTokenRefresh]);

  const value = {
    user,
    login,
    logout,
    updateUser,
    loading,
    tenantDomain,
    updateTenantDomain
  };

  if (loading) {
    // You might want to show a loading spinner here
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};